import { useAppDispatch } from '@/store/hooks';
import { updateUserDetailsFields } from '@/features/userDetails/userDetailsSlice';
import { logger } from '@/services/logger/logger';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { setComplete, clearErrorMessage, setErrorMessage } from '@/features/paymentProcess/transactionSlice';
import { ErrorCustomiser } from '@/services/error/ErrorContainer';
import SoftHider from '@/components/softHider/SoftHider';
import ButtonLoader from '@/components/loader/ButtonLoader';

import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';
import { useBraintreeApplepayInit } from './useBraintreeApplepayInit';
import { useBraintreeCreate } from './useBraintreeCreate';
import { useBraintreeExecute } from './useBraintreeExecute';

import './applepay.css';

function ApplepayButton({
  disabled = false,
  onClick = () => {},
  setProcessing = () => {},
  testId = 'paymentSelectApplepay'
}: {
  disabled?: boolean,
  onClick?: () => void,
  setProcessing?: (newProcessing: boolean) => void,
  testId?: string,
} = {}) {
  const dispatch = useAppDispatch();
  const journeyNavigate = useJourneyNavigate();
  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();

  const {
    deviceSupportsApplePay,
    braintreeApplePay,
    applepayReady,
    beginApplepaySession,
  } = useBraintreeApplepayInit();
  const { triggerCreateApi } = useBraintreeCreate();
  const { triggerExecuteApi } = useBraintreeExecute();

  if (!deviceSupportsApplePay() || disabled) {
    return null;
  }

  const onCancel = () => {
    setProcessing(false);
    logger.info('ApplePay cancelled');
  };

  const onError = (error: ApplePayJS.ApplePayError | Error) => {
    logger.error('Error on ApplePay');
    logger.error(error);

    setProcessing(false);
    dispatch(setErrorMessage('Error on ApplePay'));
    paymentFailure();
  };

  const onValidate = async (applepaySession: ApplePaySession, validateData: ApplePayJS.ApplePayValidateMerchantEvent) => {
    if (!braintreeApplePay) {
      logger.error(new Error('Error: BraintreeApplepay not set'));
      return;
    }

    try {
      const merchantSession = await braintreeApplePay.performValidation({
        validationURL: validateData.validationURL,
        displayName: 'Comic Relief',
      }) as unknown;
      applepaySession.completeMerchantValidation(merchantSession);
    } catch (error) {
      if (error && typeof error === 'object' && 'details' in error) {
        logger.error(error.details);
      }
      onError(error as Error);
      applepaySession.abort();
    }
  };

  const onPayment = async (applepaySession: ApplePaySession, paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
    if (!braintreeApplePay) {
      logger.error(new Error('Error: BraintreeApplepay not set'));
      return;
    }

    // Create payment-service transaction
    const createData = await triggerCreateApi(
      paymentData,
    );
    if (!createData?.data) {
      logger.error(new Error('Error: Create transaction API error'));
      applepaySession.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors: [
          new ApplePayError('unknown', 'name', 'Error initiating ApplePay payment'),
        ],
      });
      dispatch(setErrorMessage('Error initiating ApplePay payment'));
      journeyNavigate('error');
      return;
    }

    // Parse ApplePayment response on Braintree
    const paymentResult = await braintreeApplePay.tokenize({ token: paymentData?.payment?.token });
    if (!paymentResult) {
      logger.error(new Error('Error: Braintree Applepay Tokenize error'));
      applepaySession.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors: [
          new ApplePayError('unknown', 'name', 'Error processing ApplePay payment'),
        ],
      });
      dispatch(setErrorMessage('Error processing ApplePay payment'));
      paymentFailure();
      return;
    }

    // Execute payment-service transaction
    await triggerExecuteApi(
      paymentData,
      paymentResult.nonce,
      createData?.data,
      {
        onSuccess: () => {
          const firstName = paymentData.payment.billingContact?.givenName ?? '';
          if (firstName) {
            dispatch(updateUserDetailsFields({ firstName }));
          }
          applepaySession.completePayment({
            status: ApplePaySession.STATUS_SUCCESS,
            errors: [],
          });
          dispatch(setComplete(true));
          dispatch(clearErrorMessage());
          paymentSuccess();
        },
        onError: (error) => {
          logger.error(new Error('Error: Execute transaction API error'));
          const errorMessage = ErrorCustomiser(error, 'Error executing ApplePay payment');
          applepaySession.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: [
              new ApplePayError('unknown', 'name', errorMessage),
            ],
          });
          dispatch(setErrorMessage(errorMessage));
          paymentFailure();
        },
      }
    );
  };

  const onBeginPayment = () => {
    onClick();

    beginApplepaySession(onValidate, onPayment, onCancel);

    setProcessing(true);
  };

  return (
    <SoftHider show={applepayReady} loader={<ButtonLoader />}>
      <button
        type="button"
        className="apple-pay-button apple-pay-button-black"
        onClick={() => { onBeginPayment(); }}
        data-test={testId}
      >
        {' '}
      </button>
    </SoftHider>
  );
}

export default ApplepayButton;
